/**
 * SearchableSelect — type-to-filter dropdown that bounds to its parent
 * surface instead of escaping the viewport.
 *
 * Native `<select>` with the 250+ country list is unworkable on every
 * platform: macOS / iOS render the dropdown at viewport level (escapes
 * any modal it's inside), can't be searched beyond a single keystroke,
 * and overflows the screen vertically on mobile and on small modals.
 * This component renders an in-flow listbox sized to its container so
 * scroll happens INSIDE the picker, not OVER the page.
 *
 * Behavior:
 *   - Click the input → opens the list (optional `pinCode` row at top,
 *     remaining options preserved in source order).
 *   - Type → filters by visible label OR code, case-insensitive.
 *   - Arrow keys → move the active row; Enter selects; Escape closes.
 *   - Click outside → closes without changing the value.
 *   - max-height: 240px on the panel; vertical scroll inside.
 *
 * Used by ChallengeLoginModal + ProfileCompletionModal for both the
 * country and state pickers (states pass `pinCode={undefined}` since
 * they're already short and have no priority code).
 */
import React, {CSSProperties, ReactElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import "./SearchableSelect.scss";

export interface SearchableSelectOption {
    code: string;
    label: string;
}

interface Props {
    value: string;
    onChange: (next: string) => void;
    options: ReadonlyArray<SearchableSelectOption>;
    pinCode?: string;
    placeholder?: string;
    disabled?: boolean;
    ariaLabel?: string;
    className?: string;
    style?: CSSProperties;
    /*
     * Tap-to-open, no typing. Renders the input `readOnly` so tapping it opens the
     * list WITHOUT raising the on-screen keyboard. Used on touch devices where the
     * keyboard shrinks the visual viewport and reflows a fixed modal around the
     * picker. Type-to-filter is disabled while this is set (the caller only turns
     * it on for touch); arrow-key/scroll selection still works.
     */
    noKeyboard?: boolean;
}

export function SearchableSelect({
    value,
    onChange,
    options,
    pinCode,
    placeholder = "Select…",
    disabled,
    ariaLabel,
    className,
    style,
    noKeyboard,
}: Props): ReactElement {
    /*
     * `ordered` floats the optional pinCode entry to the top while
     * preserving source order for the rest. Caller controls priority
     * via the prop so this same component can pin "US" on the country
     * picker and pin nothing on the state picker.
     */
    const ordered = useMemo<SearchableSelectOption[]>(() => {
        if (!pinCode) return [...options];
        const pinned = options.find(o => o.code === pinCode);
        const rest = options.filter(o => o.code !== pinCode);
        return pinned ? [pinned, ...rest] : [...rest];
    }, [options, pinCode]);

    const selected = useMemo(() => ordered.find(o => o.code === value), [ordered, value]);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const listboxId = `searchable-select-listbox-${useId()}`;

    /*
     * Portaled dropdown position. The list is rendered into document.body via a
     * portal and positioned `fixed` to the field's on-screen rect — NOT absolutely
     * inside the field. Rendered in-flow inside the modal, an open list (up to
     * 240px, dropping below a mid-modal field) counts toward the modal's scrollable
     * height and inflates/oversizes the modal (`_content` is overflow-y:auto,
     * max-height:90vh on mobile). A fixed portal overlays on top without being
     * modal content, so opening it can never change the modal's size.
     */
    const [menuRect, setMenuRect] = useState<{ left: number; top: number; width: number } | null>(null);
    useLayoutEffect(() => {
        if (!open) {
            setMenuRect(null);
            return;
        }
        const reposition = (): void => {
            const el = rootRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            setMenuRect({left: r.left, top: r.bottom + 4, width: r.width});
        };
        reposition();
        /*
         * `capture: true` so we also catch scrolls on the modal's inner scroll
         * container (a bubbling scroll listener on window misses nested scrollers).
         */
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open]);

    const filtered = useMemo<SearchableSelectOption[]>(() => {
        const q = query.trim().toLowerCase();
        if (!q) return ordered;
        return ordered.filter(o =>
            o.label.toLowerCase().includes(q) ||
            o.code.toLowerCase().includes(q));
    }, [ordered, query]);

    /*
     * Reset the active highlight to the top every time the filter
     * changes so the user can hit Enter on the first match without
     * arrow-key gymnastics.
     */
    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    /*
     * Close on outside click. Listening on `mousedown` (not `click`)
     * fires before the input regains focus, so a click on an option
     * that's inside the panel still commits cleanly.
     */
    useEffect(() => {
        if (!open) return;
        function onDocMouseDown(e: MouseEvent): void {
            if (!rootRef.current) return;
            const target = e.target as Node;
            /*
             * The list is portaled to document.body — OUTSIDE rootRef — so a click
             * on an option would otherwise register as "outside" and close the menu
             * before the option's own mousedown can commit. Exclude the list too.
             */
            if (!rootRef.current.contains(target) && !listRef.current?.contains(target)) {
                setOpen(false);
                setQuery("");
            }
        }
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [open]);

    /*
     * Keep the active row in view as the user arrow-keys through the
     * list. Plain scrollIntoView would jump the page when the picker
     * isn't the topmost scroll container; manual scrollTop math keeps
     * the scroll local to the listbox.
     */
    useEffect(() => {
        if (!open) return;
        const list = listRef.current;
        if (!list) return;
        const row = list.children[activeIndex] as HTMLElement | undefined;
        if (!row) return;
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;
        if (rowTop < list.scrollTop) {
            list.scrollTop = rowTop;
        } else if (rowBottom > list.scrollTop + list.clientHeight) {
            list.scrollTop = rowBottom - list.clientHeight;
        }
    }, [open, activeIndex]);

    function commitChoice(code: string): void {
        onChange(code);
        setOpen(false);
        setQuery("");
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) {
                setOpen(true);
                return;
            }
            setActiveIndex(i => Math.min(i + 1, Math.max(0, filtered.length - 1)));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            if (open && filtered[activeIndex]) {
                e.preventDefault();
                commitChoice(filtered[activeIndex].code);
            }
        } else if (e.key === "Escape") {
            if (open) {
                e.preventDefault();
                setOpen(false);
                setQuery("");
            }
        }
    }

    /*
     * When open, the input shows the live query (so the user sees what
     * they're typing). When closed, it shows the selected label (so the
     * field reads like a normal picker). Empty selection → placeholder.
     */
    const displayValue = open && !noKeyboard ? query : selected?.label ?? "";

    const rootClass = ["searchable-select", className, disabled ? "searchable-select--disabled" : ""]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={rootClass} ref={rootRef} style={style}>
            <input
                type="text"
                className="searchable-select__input"
                value={displayValue}
                /*
                 * readOnly on touch (noKeyboard) so tapping opens the list without
                 * raising the on-screen keyboard — a readOnly input is still
                 * focusable and fires onFocus, so the tap-to-open path is intact.
                 * inputMode="none" belt-and-suspenders for browsers that would
                 * still surface a keyboard for a focused readOnly field.
                 */
                readOnly={noKeyboard}
                inputMode={noKeyboard ? "none" : undefined}
                onChange={(e) => {
                    if (noKeyboard) return;
                    setQuery(e.target.value);
                    setOpen(true);
                }}
                /*
                 * Open on an explicit pointer interaction (mouse click / touch
                 * tap), NOT on bare focus. Focus-trapped dialogs (widgets/Modal)
                 * move focus to their first focusable element on open — with
                 * open-on-focus, a picker rendered first would auto-expand the
                 * instant the modal opened. Pointer-down keeps click-to-open and
                 * touch tap-to-open intact (the input is readOnly on touch, so no
                 * keyboard); keyboard users open the list via the arrow keys
                 * (onKeyDown) or by typing (onChange), matching WAI-ARIA combobox
                 * behaviour where focus alone does not open the listbox.
                 */
                onPointerDown={() => {
                    if (!disabled) setOpen(true);
                }}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                role="combobox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-label={ariaLabel}
                autoComplete="off"
            />
            <span className="searchable-select__chevron" aria-hidden="true">▾</span>
            {open && !disabled && menuRect && createPortal(
                <ul
                    id={listboxId}
                    role="listbox"
                    className="searchable-select__list"
                    ref={listRef}
                    aria-label={ariaLabel}
                    /*
                     * Fixed to the field's on-screen rect, portaled to <body> so the
                     * open list never counts as modal content (see menuRect above).
                     * Overrides the stylesheet's absolute/left:0/right:0 positioning.
                     * z-index sits above the modal container (99999).
                     */
                    style={{
                        position: "fixed",
                        left: menuRect.left,
                        top: menuRect.top,
                        width: menuRect.width,
                        right: "auto",
                        zIndex: 100001,
                    }}
                >
                    {filtered.length === 0 &&
                        <li className="searchable-select__empty">No matches.</li>
                    }
                    {filtered.map((o, idx) =>
                        <li
                            key={o.code}
                            role="option"
                            aria-selected={o.code === value}
                            className={`searchable-select__option${
                                idx === activeIndex ? " searchable-select__option--active" : ""
                            }${o.code === value ? " searchable-select__option--selected" : ""}`}
                            onMouseDown={(e) => {
                                /*
                                 * mousedown not click — the input's blur
                                 * would otherwise fire first and close
                                 * the list before click could land.
                                 */
                                e.preventDefault();
                                commitChoice(o.code);
                            }}
                            onMouseEnter={() => setActiveIndex(idx)}
                        >
                            {o.label}
                        </li>)}
                </ul>,
                document.body,
            )}
        </div>
    );
}
